import type { BoardKind, ColumnCreatePayload } from "@/lib/types";

export type BoardTemplate = "standard" | "ticketing" | "ideenbox";

export interface BoardTemplateDef {
  id: BoardTemplate;
  label: string;
  description: string;
  kind: BoardKind;
  columns: ColumnCreatePayload[];
}

// Per-template column lists. Position values are float so future re-ordering
// can squeeze new columns between without re-numbering.
const STANDARD_COLUMNS: ColumnCreatePayload[] = [
  { name: "Titel", type: "text", position: 1000 },
  {
    name: "Status",
    type: "status",
    position: 2000,
    config: {
      options: [
        { id: "offen", label: "Offen", color: "slate", role: "open" },
        { id: "in-arbeit", label: "In Arbeit", color: "blue", role: "in_progress" },
        { id: "erledigt", label: "Erledigt", color: "green", role: "closed" },
      ],
    },
  },
  {
    name: "Verantwortlich",
    type: "person",
    position: 3000,
    config: { allow_multiple: true },
  },
  { name: "Fällig", type: "date", position: 4000 },
];

const TICKETING_COLUMNS: ColumnCreatePayload[] = [
  { name: "Titel", type: "text", position: 1000 },
  { name: "Beschreibung", type: "long_text", position: 2000 },
  {
    name: "Status",
    type: "status",
    position: 3000,
    config: {
      // Expanded status ladder (Phase 0). Multiple options share a role; the
      // workflow actions target a specific option id. Coarse "Status"
      // (Zu erledigen / In Arbeit / Fertig) is derived from role, never stored.
      // Stable ids are PRESERVED so existing cells keep resolving.
      options: [
        { id: "offen", label: "Offen", color: "red", role: "open" },
        { id: "gesichtet", label: "Gesichtet", color: "blue", role: "open" },
        { id: "eingeplant", label: "Eingeplant", color: "purple", role: "open" },
        { id: "in-bearbeitung", label: "In Bearbeitung", color: "blue", role: "in_progress" },
        { id: "in-review", label: "In Review", color: "teal", role: "in_progress" },
        { id: "wartet", label: "Warten auf Feedback", color: "amber", role: "waiting" },
        { id: "zurueckgestellt", label: "Zurückgestellt", color: "yellow", role: "waiting" },
        // Two terminal outcomes, both role "closed": "Erledigt" (resolved) and
        // "Nicht relevant" (dismissed / self-close).
        { id: "geloest", label: "Erledigt", color: "green", role: "closed" },
        { id: "nicht-relevant", label: "Nicht relevant", color: "slate", role: "closed" },
      ],
    },
  },
  {
    // Issue type — a label-only category (NO roles, so it's not the workflow
    // status; positioned AFTER Status so _find_status_column still picks the
    // lifecycle column). Does NOT branch workflow or fields. "Projekt" is a
    // label here; project-as-container is a separate future concern.
    name: "Vorgangstyp",
    type: "status",
    position: 3250,
    config: {
      required: true,
      options: [
        { id: "bug", label: "Bug / Problem", color: "red" },
        { id: "aufgabe", label: "Aufgabe / Anpassung", color: "blue" },
        { id: "ll-it", label: "LL IT", color: "purple" },
        { id: "projekt", label: "Projekt", color: "teal" },
        { id: "vorschlag", label: "Vorschlag", color: "green" },
      ],
    },
  },
  {
    // Category/area — a required single-select on the submit form (no roles, so
    // it's NOT the workflow status). Positioned after Status so the lifecycle
    // column stays the "workflow status". "Sonstiges" is the escape hatch.
    name: "Bereich",
    type: "status",
    position: 3500,
    config: {
      required: true,
      options: [
        { id: "website", label: "Website", color: "blue" },
        { id: "rs2", label: "rs2", color: "purple" },
        { id: "dynamics", label: "Dynamics", color: "teal" },
        { id: "hardware", label: "Hardware", color: "amber" },
        { id: "telefonie", label: "Telefonie", color: "green" },
        { id: "sonstiges", label: "Sonstiges", color: "slate" },
      ],
    },
  },
  {
    name: "Priorität",
    type: "status",
    position: 4000,
    config: {
      // Ordered low→high. Flow sorts rows by the option array order, so the
      // ORDER is the priority rank (no separate rank field needed).
      // "Betriebsverhindernd" is a SEPARATE severity flag (checkbox below),
      // never a 6th priority level.
      options: [
        { id: "sehr-niedrig", label: "Sehr niedrig", color: "slate" },
        { id: "niedrig", label: "Niedrig", color: "blue" },
        { id: "normal", label: "Normal", color: "teal" },
        { id: "hoch", label: "Hoch", color: "amber" },
        { id: "sehr-hoch", label: "Sehr hoch", color: "red" },
      ],
    },
  },
  {
    name: "Zugewiesen an",
    type: "person",
    position: 5000,
    config: { allow_multiple: true },
  },
  { name: "Fällig", type: "date", position: 6000 },
  // Read-only ticket meta: who raised it + their department + company
  // (snapshotted at submit). Right edge by default; hide/reorder via Felder.
  { name: "Ersteller", type: "creator", position: 7000 },
  { name: "Abteilung", type: "creator_department", position: 8000 },
  { name: "Unternehmen", type: "creator_company", position: 9000 },
];

const IDEENBOX_COLUMNS: ColumnCreatePayload[] = [
  { name: "Titel", type: "text", position: 1000 },
  { name: "Beschreibung", type: "long_text", position: 2000 },
  { name: "Anhänge", type: "file", position: 3000 },
  {
    name: "Status",
    type: "status",
    position: 4000,
    config: {
      options: [
        { id: "neu", label: "Neu", color: "slate", role: "open" },
        { id: "in-pruefung", label: "In Prüfung", color: "blue", role: "in_progress" },
        { id: "akzeptiert", label: "Akzeptiert", color: "green", role: "closed" },
        { id: "umgesetzt", label: "Umgesetzt", color: "teal", role: "closed" },
        { id: "nicht-akzeptiert", label: "Nicht Akzeptiert", color: "red", role: "closed" },
      ],
    },
  },
  // Proxy submitter for colleagues without a Flow/Keycloak account.
  { name: "Idee im Namen von", type: "text", position: 5000 },
  // The single person column → the assignment target (_find_person_column).
  { name: "Gutachter", type: "person", position: 6000, config: { allow_multiple: true } },
  // Read-only submitter meta (does not occupy the person-assignment slot).
  { name: "Eingereicht von", type: "creator", position: 7000 },
];

export const BOARD_TEMPLATES: Record<BoardTemplate, BoardTemplateDef> = {
  standard: {
    id: "standard",
    label: "Standard",
    description: "Titel, Status, Verantwortlich, Fällig — der unkomplizierte Start.",
    kind: "project",
    columns: STANDARD_COLUMNS,
  },
  ticketing: {
    id: "ticketing",
    label: "Ticketing",
    description: "Vorbereitet für Reklamationen, Anfragen und interne Tickets.",
    kind: "tickets",
    columns: TICKETING_COLUMNS,
  },
  ideenbox: {
    id: "ideenbox",
    label: "Ideenbox",
    description: "Mitarbeiter-Ideen sammeln, bewerten und beantworten.",
    kind: "ideas",
    columns: IDEENBOX_COLUMNS,
  },
};

export const BOARD_TEMPLATE_ORDER: BoardTemplate[] = ["standard", "ticketing", "ideenbox"];
